import React, { Suspense, useEffect, useState } from "react";
import {
  Await,
  type Navigation,
  useLoaderData,
  useLocation,
  useNavigation,
  useRouteLoaderData,
} from "react-router";
import { Icon, IconName } from "~/components/ui/icon";
import { UserDropdown } from "~/components/features/user-dropdown";
import { ThemeSwitch } from "~/routes/resources+/theme-switch";
import { BRAND_NAME } from "~/lib/utils/constants";
import { cn } from "~/lib/utils";
import { ScrollArea } from "~/components/ui/scroll-area";
import { capitalize } from "~/lib/utils/helpers/string";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "~/components/ui/navigation-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "~/components/ui/sheet";
import { Button } from "~/components/ui/button";
import { Menu } from "lucide-react";
import { loader } from "~/root";
import {
  filterHeaderLinksByTeamRole,
  filterSidebarLinksByTeamRole,
} from "~/lib/utils/auth";
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from "~/components/ui/sidebar";
import type { Team, Terminal } from "~/lib/db/schema";
import { getTeamImgSrc } from "~/lib/utils/misc";
import { TenantLink } from "~/components/tenant-link";
import {
  headerLinks,
  sidebarLinks,
  valhallaLinks,
} from "~/lib/utils/nav-links";
import { getIsAdmin, useRoles } from "~/lib/utils/roles";
import { Badge } from "../ui/badge";
import { getRouteSkeleton } from "~/lib/utils/get-route-skeleton";

let currentOptimisticPath: string | null = null;

// const routesThatShouldShowSkeletonOnNavigation = ["/dashboard"];

// Function to check if navigation stays within the same section
const isNavigatingWithinSameSection = (navigation: Navigation) => {
  // Only check if we're actively navigating
  if (navigation.state === "idle" || !navigation.location?.pathname) {
    return false;
  }

  // // Force showing the skeleton on some routes
  // if (
  //   routesThatShouldShowSkeletonOnNavigation.some(
  //     (route) => route === navigation.location.pathname
  //   )
  // ) {
  //   return false;
  // }

  // Get the top-level section from current and destination paths
  const getCurrentSection = (path: string) => {
    // Extract first part of the path (e.g. "/terminals/123" -> "terminals")
    const matches = path.match(/^\/([^/]+)/);
    return matches ? matches[1] : "";
  };

  const currentSection = getCurrentSection(location.pathname);
  const targetSection = getCurrentSection(navigation.location.pathname);

  // If both sections are the same and not empty, we're navigating within the same section
  return currentSection !== "" && currentSection === targetSection;
};

export const DefaultLayout = ({
  children,
  teamPromise,
}: {
  children: React.ReactNode;
  teamPromise?: Promise<Team | undefined>;
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const data = useLoaderData<typeof loader>();
  const rootData = useRouteLoaderData("root") as
    | {
        terminals?: Terminal[];
        team?: Team;
        isValhallaUser?: boolean;
      }
    | undefined;

  const { isValhallaUser, terminals, team } = rootData || {};

  const location = useLocation();
  const navigation = useNavigation();
  const incomingPathname = navigation.location?.pathname || location.pathname;

  const { globalRole, teamRole } = useRoles();

  const isInValhalla = isValhallaUser && location.pathname === "/valhalla";

  const shouldShowSkeleton =
    navigation.state !== "idle" && !isNavigatingWithinSameSection(navigation);

  const filteredHeaderLinks =
    globalRole === "admin"
      ? headerLinks
      : filterHeaderLinksByTeamRole(teamRole);
  const filteredSidebarLinks =
    globalRole === "admin"
      ? sidebarLinks
      : filterSidebarLinksByTeamRole(teamRole);

  // Team logo skeleton fallback - matches content structure exactly
  const TeamLogoSkeleton = ({ isMobile = false }: { isMobile?: boolean }) => (
    <div
      className={cn(
        "flex items-center h-full gap-3 flex-shrink-0",
        !isMobile && "ml-9 md:ml-0"
      )}
    >
      <div className="h-[35px] w-[35px] flex-shrink-0 bg-muted/30 rounded animate-pulse" />
      <div className="h-5 w-24 flex-shrink-0 bg-muted/30 rounded animate-pulse" />
    </div>
  );

  // Header skeleton - shows both logo and nav menu skeletons together
  const HeaderSkeleton = ({ isMobile = false }: { isMobile?: boolean }) => (
    <>
      <TeamLogoSkeleton isMobile={isMobile} />
      <div className="hidden md:flex items-center gap-2">
        {filteredHeaderLinks.map((_, index) => (
          <div
            key={index}
            className="h-9 w-20 bg-muted/30 rounded animate-pulse"
          />
        ))}
      </div>
    </>
  );

  // Header content - shows both logo and nav menu together
  const HeaderContent = ({
    teamData,
    className,
    isMobile = false,
    isBeta = false,
  }: {
    teamData?: Team;
    className?: string;
    isMobile?: boolean;
    isBeta?: boolean;
  }) => (
    <>
      <TeamLogoContent
        teamData={teamData}
        className={className}
        isMobile={isMobile}
        isBeta={isBeta}
      />
      <NavigationMenu className="hidden md:block">
        <NavigationMenuList className="gap-2">
          {filteredHeaderLinks.map(({ item, className, links }) => (
            <NavigationMenuItem key={item}>
              <NavigationMenuTrigger className="bg-transparent">
                {item}
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className={className}>
                  {links.map(({ to, name, iconName, description }) => (
                    <ListItem
                      key={to}
                      href={to}
                      title={name}
                      icon={<Icon name={iconName as IconName} />}
                    >
                      {description}
                    </ListItem>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
          ))}
        </NavigationMenuList>
      </NavigationMenu>
    </>
  );

  // Team logo content component
  const TeamLogoContent = ({
    teamData,
    className,
    isMobile = false,
    isBeta = false,
  }: {
    teamData?: Team;
    className?: string;
    isMobile?: boolean;
    isBeta?: boolean;
  }) => {
    const teamName = (teamData?.name || BRAND_NAME).toUpperCase();
    const teamLogoSrc = teamData?.images
      ? getTeamImgSrc(
          teamData.images.find((image) => image.type === "logo")?.id!,
          "logo"
        )
      : null;

    return (
      <TenantLink
        to="/dashboard"
        className={cn(
          "flex items-center h-full gap-3 font-black cursor-pointer flex-shrink-0",
          !isMobile && "ml-9 md:ml-0"
        )}
      >
        {teamLogoSrc && (
          <img
            src={teamLogoSrc}
            alt={teamName}
            className={cn(
              "h-[35px] flex-shrink-0 dark:filter dark:grayscale dark:brightness-0 dark:invert",
              className
            )}
          />
        )}
        <span className="whitespace-nowrap">{teamName}</span>
        {isBeta && (
          <p className="text-[7px] -ml-2 -mt-[7px] flex-shrink-0">BETA</p>
        )}
      </TenantLink>
    );
  };

  const renderTeamLogo = ({
    className,
    isMobile = false,
    isBeta = false,
  }: {
    className?: string;
    isMobile?: boolean;
    isBeta?: boolean;
  }) => {
    // If we have direct team data (public routes), use it
    if (team) {
      return (
        <TeamLogoContent
          teamData={team}
          className={className}
          isMobile={isMobile}
          isBeta={isBeta}
        />
      );
    }

    // If we have a promise, use Suspense/Await
    if (teamPromise) {
      return (
        <Suspense fallback={<TeamLogoSkeleton isMobile={isMobile} />}>
          <Await resolve={teamPromise}>
            {(resolvedTeam) => (
              <TeamLogoContent
                teamData={resolvedTeam}
                className={className}
                isMobile={isMobile}
                isBeta={isBeta}
              />
            )}
          </Await>
        </Suspense>
      );
    }

    // Fallback - show skeleton
    return <TeamLogoSkeleton isMobile={isMobile} />;
  };

  // Render the full header (logo + nav) with streaming support
  const renderHeader = () => {
    // If we have direct team data (public routes), render immediately
    if (team) {
      return <HeaderContent teamData={team} isBeta={true} />;
    }

    // If we have a promise, use Suspense/Await to load both together
    if (teamPromise) {
      return (
        <Suspense fallback={<HeaderSkeleton />}>
          <Await resolve={teamPromise}>
            {(resolvedTeam) => (
              <HeaderContent teamData={resolvedTeam} isBeta={true} />
            )}
          </Await>
        </Suspense>
      );
    }

    // Fallback - show skeleton
    return <HeaderSkeleton />;
  };

  useEffect(() => {
    // Clear the optimistic path when navigation completes
    if (navigation.state === "idle") {
      // Reset the currentOptimisticPath when navigation is complete
      // This ensures we're always using the actual route for active state
      currentOptimisticPath = null;
    }

    // Handle navigation events
    if (navigation.state !== "idle" && navigation.location) {
      // If navigating to a terminals route, set terminals as active
      if (navigation.location.pathname.startsWith("/terminals")) {
        currentOptimisticPath = "/terminals";
      }
      // If navigating AWAY from terminals, explicitly clear it
      else if (location.pathname.startsWith("/terminals")) {
        currentOptimisticPath = navigation.location.pathname;
      }
      // For all other navigation, set the path we're navigating to
      else {
        currentOptimisticPath = navigation.location.pathname;
      }
    }
  }, [navigation.state, navigation.location, location.pathname]);

  return (
    <div
      className={cn(
        "relative grid grid-cols-[auto] grid-rows-[var(--header-height)_auto] md:grid-cols-[var(--sidebar-width)_auto_8px] h-dvh w-full bg-accent overflow-hidden transition-colors duration-700 ease-in-out",
        isInValhalla && "bg-background"
      )}
    >
      {/* Mobile Sidebar */}
      <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden fixed top-3 left-1.5 z-[502]"
          >
            <Menu className="h-6 w-6" />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="left"
          className="z-[502] w-screen p-0"
          overlayClassName="z-[501]"
        >
          <SheetHeader className="p-4">
            <SheetTitle>
              {renderTeamLogo({ isMobile: true, isBeta: true })}
            </SheetTitle>
          </SheetHeader>
          <ScrollArea className="h-content-height">
            <SidebarProvider>
              <SidebarContent>
                <ScrollArea className="h-content-height [&_div:last-child]:mb-4">
                  {/* Main navigation */}
                  <MobileNavigationGroup
                    links={filteredSidebarLinks}
                    isActive={(to) => location.pathname.startsWith(to)}
                    onClick={() => setIsMobileMenuOpen(false)}
                  />

                  {/* Terminals section */}
                  {terminals && terminals.length > 0 && (
                    <MobileNavigationGroup
                      label="TERMINALES"
                      links={terminals.map(({ id, name }: Terminal) => ({
                        to: `/terminals/${id}`,
                        name: capitalize(name),
                        iconName: "factory",
                      }))}
                      isActive={(to) => location.pathname === to}
                      onClick={() => setIsMobileMenuOpen(false)}
                    />
                  )}

                  {/* Dynamic sections based on headerLinks */}
                  {filteredHeaderLinks.map((section, index) => {
                    const isAdmin = getIsAdmin({ teamRole });

                    // Skip rendering if it's Admin section and user is not admin
                    if (section.item === "Admin" && !isAdmin) {
                      return null;
                    }

                    return (
                      <MobileNavigationGroup
                        key={section.item}
                        label={section.item.toUpperCase()}
                        links={section.links}
                        isActive={(to) => location.pathname === to}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={cn(
                          index === filteredHeaderLinks.length - 1 && "pb-8"
                        )}
                      />
                    );
                  })}
                </ScrollArea>
              </SidebarContent>
            </SidebarProvider>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Header */}
      <header className="z-[500] col-[1/-1] row-[1/2] md:col-[1/4] overflow-hidden md:overflow-visible">
        <nav className="relative flex items-center justify-between h-full gap-4 md:gap-10 px-5">
          <div className="flex items-center gap-10 min-w-0 flex-1 md:flex-initial">
            {!isInValhalla && renderHeader()}
          </div>

          <div className="flex items-center gap-4 text-xl flex-shrink-0">
            {isValhallaUser && (
              <TenantLink
                to={isInValhalla ? "/" : "/valhalla"}
                className="flex items-center"
              >
                <Badge>{isInValhalla ? "App" : "Valhalla"}</Badge>
              </TenantLink>
            )}
            <ThemeSwitch userPreference={data.requestInfo.userPrefs.theme} />
            <UserDropdown />
          </div>
        </nav>
      </header>

      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "hidden md:block col-[1/2] row-[2/3] pt-5",
          isValhallaUser &&
            "pt-0 md:flex flex-col justify-center -mt-header-height"
        )}
      >
        <nav role="navigation" className="h-dvh pt-header-height">
          <ul className="flex flex-col items-center justify-center gap-3">
            {(isInValhalla ? valhallaLinks : filteredSidebarLinks).map(
              ({ to, name, iconName }) => {
                const isActive = location.pathname.startsWith(to);

                if (to === "/terminals") {
                  // Check if we're currently on the terminals route
                  const isTerminalsActive =
                    location.pathname === "/terminals" ||
                    location.pathname.startsWith("/terminals/");

                  // Check if we're navigating to terminals
                  const isNavigatingToTerminals =
                    navigation.state !== "idle" &&
                    (navigation.location?.pathname === "/terminals" ||
                      (navigation.location?.pathname || "").startsWith(
                        "/terminals/"
                      ));

                  // Check if we're navigating AWAY from terminals
                  const isNavigatingAwayFromTerminals =
                    navigation.state !== "idle" &&
                    isTerminalsActive &&
                    navigation.location?.pathname &&
                    !navigation.location.pathname.startsWith("/terminals");

                  // The final active state should be true ONLY when:
                  // 1. We're currently on a terminals route AND not navigating away
                  // 2. OR we're navigating TO a terminals route
                  const shouldShowActive =
                    (isTerminalsActive && !isNavigatingAwayFromTerminals) ||
                    isNavigatingToTerminals;

                  return (
                    <li key={to} className="flex flex-col justify-center">
                      <TooltipProvider delayDuration={200}>
                        <Tooltip
                          open={isTooltipOpen}
                          onOpenChange={setIsTooltipOpen}
                        >
                          <TooltipTrigger asChild>
                            <TenantLink
                              to={to}
                              onClick={() => {
                                // DO NOT preventDefault - let the Link handle navigation naturally

                                // Only set the terminals path as active if we're navigating TO terminals
                                // DON'T set it when navigating away
                                if (!isNavigatingAwayFromTerminals) {
                                  currentOptimisticPath = "/terminals";
                                } else {
                                  // When navigating away, actively clear it
                                  currentOptimisticPath = null;
                                }

                                // Force update UI
                                setIsTooltipOpen(false);

                                // DO NOT navigate programmatically - let the Link component handle it
                              }}
                              className="group flex flex-col gap-1 items-center justify-center text-center text-[10px] leading-3 leading max-w-10"
                            >
                              <span
                                className={cn(
                                  "rounded-lg p-1.5 transition-colors duration-200",
                                  {
                                    "text-background bg-foreground":
                                      shouldShowActive,
                                    "group-hover:bg-foreground/10":
                                      !shouldShowActive,
                                  }
                                )}
                              >
                                <Icon
                                  name={iconName}
                                  className="w-sidebar-icon-height h-sidebar-icon-height"
                                />
                              </span>
                              {name}
                            </TenantLink>
                          </TooltipTrigger>
                          <TooltipContent
                            side="right"
                            align="start"
                            alignOffset={10}
                            sideOffset={4}
                            className="flex flex-col p-1 text-xs bg-muted shadow-lg border border-muted-foreground/10"
                          >
                            {terminals?.map(({ id, name }: Terminal) => (
                              <TenantLink
                                key={id}
                                to={`/terminals/${id}`}
                                className="relative text-foreground cursor-pointer flex select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 hover:bg-background"
                                onClick={() => {
                                  setIsTooltipOpen(false);
                                  currentOptimisticPath = "/terminals";
                                }}
                              >
                                {capitalize(name)}
                              </TenantLink>
                            ))}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </li>
                  );
                }

                return (
                  <li key={to} className="flex flex-col justify-center">
                    <OptimisticNavLink
                      to={to}
                      isActive={isActive}
                      className="group flex flex-col gap-1 items-center justify-center text-center text-[10px] leading-3 leading max-w-10"
                    >
                      {(isOptimisticallyActive: boolean) => (
                        <>
                          <span
                            className={cn(
                              "rounded-lg p-1.5 transition-colors duration-200",
                              {
                                "text-background bg-foreground":
                                  isOptimisticallyActive,
                                "group-hover:bg-foreground/10":
                                  !isOptimisticallyActive,
                              }
                            )}
                          >
                            <Icon
                              name={iconName}
                              className="w-sidebar-icon-height h-sidebar-icon-height"
                            />
                          </span>
                          {name}
                        </>
                      )}
                    </OptimisticNavLink>
                  </li>
                );
              }
            )}
          </ul>
        </nav>
      </aside>

      {/* Main Content */}
      <div className="col-[1/-1] row-[2/3] md:col-[2/3] md:row-[2/3] h-full md:rounded-t-xl bg-background overflow-y-auto overflow-x-hidden">
        {shouldShowSkeleton
          ? getRouteSkeleton(incomingPathname, terminals as Terminal[])
          : children}
      </div>
    </div>
  );
};

const ListItem = React.forwardRef<
  React.ElementRef<"a">,
  React.ComponentPropsWithoutRef<"a"> & {
    icon?: React.ReactNode;
  }
>(({ className, title, icon, children, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
            className
          )}
          {...props}
        >
          <div
            className={cn(
              "text-sm font-medium leading-none",
              title && "flex items-center gap-1.5 text-sm"
            )}
          >
            {icon}
            {title}
          </div>
          <p className="text-sm leading-snug line-clamp-2 text-muted-foreground">
            {children}
          </p>
        </a>
      </NavigationMenuLink>
    </li>
  );
});

ListItem.displayName = "ListItem";

const MobileMenuLink = ({
  to,
  name,
  iconName,
  isActive,
  onClick,
}: {
  to: string;
  name: string;
  iconName?: string;
  isActive: boolean;
  onClick: () => void;
}) => (
  <SidebarMenuItem key={name}>
    <SidebarMenuButton
      isActive={isActive}
      onClick={onClick}
      className="text-base sm:text-sm h-10"
      asChild
    >
      <TenantLink to={to} className="gap-3 [&>svg]:h-5 [&>svg]:w-5">
        {iconName && <Icon name={iconName as IconName} />}
        <span>{name}</span>
      </TenantLink>
    </SidebarMenuButton>
  </SidebarMenuItem>
);

const MobileNavigationGroup = ({
  label,
  links,
  isActive,
  onClick,
  className,
}: {
  label?: string;
  links: Array<{ to: string; name: string; iconName?: string }>;
  isActive: (to: string) => boolean;
  onClick: () => void;
  className?: string;
}) => (
  <SidebarGroup className={cn("p-4 gap-1", className)}>
    {label && (
      <SidebarGroupLabel className="text-sm">{label}</SidebarGroupLabel>
    )}

    <SidebarMenu>
      {links.map((link) => (
        <MobileMenuLink
          key={link.name}
          {...link}
          isActive={isActive(link.to)}
          onClick={onClick}
        />
      ))}
    </SidebarMenu>
  </SidebarGroup>
);

const OptimisticNavLink = ({
  to,
  className,
  children,
  isActive: isActiveProp = false,
  onClick: externalOnClick,
}: {
  to: string;
  className?: string;
  children: ((isActive: boolean) => React.ReactNode) | React.ReactNode;
  isActive?: boolean;
  onClick?: (e?: React.MouseEvent<HTMLAnchorElement>) => void;
}) => {
  const location = useLocation();
  const navigation = useNavigation();
  const [, forceUpdate] = useState<{}>({});

  // Check if we're currently navigating to this link
  const isNavigatingHere =
    navigation.state !== "idle" && navigation.location?.pathname === to;

  // Calculate active state with more specific conditions
  const isActive =
    // We're on the actual route (current URL matches)
    (isActiveProp && currentOptimisticPath === null) ||
    // We're navigating to this route (optimistic UI)
    currentOptimisticPath === to ||
    // We're actually navigating to this route right now
    isNavigatingHere ||
    // Special edge case: handle terminals navigation
    (to === "/terminals" && location.pathname.startsWith("/terminals/"));

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Update the global optimistic path immediately on click
    currentOptimisticPath = to;

    // Force update to reflect the change synchronously
    forceUpdate({});

    // Call the external onClick if provided
    if (externalOnClick) {
      externalOnClick(e);
    }

    // Let the Link component handle navigation naturally with viewTransition
  };

  return (
    <TenantLink to={to} onClick={handleClick} className={className}>
      {typeof children === "function"
        ? (children as (isActive: boolean) => React.ReactNode)(isActive)
        : children}
    </TenantLink>
  );
};
